// pages/Comedor.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Badge,
  Statistic,
  Card,
  Divider,
  Alert,
  Spin,
  Drawer,
  Grid,
  Dropdown,
  Menu,
  Avatar,
  List,
  Tabs,
  Segmented,
  FloatButton,
  Popconfirm,
  Skeleton,
  Empty,
  Progress,
  Flex,
  ConfigProvider,
  Collapse,
  Popover
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  TeamOutlined,
  ExportOutlined,
  CalendarOutlined,
  CoffeeOutlined,
  HomeOutlined,
  FilterOutlined,
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
  SortAscendingOutlined,
  AppstoreOutlined,
  MenuOutlined,
  DownOutlined,
  UpOutlined,
  MobileOutlined,
  TabletOutlined,
  DesktopOutlined,
  MoreOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  WhatsAppOutlined,
  StarOutlined,
  SwapOutlined,
  ClockCircleOutlined,
  SmileOutlined,
  GroupOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const Comedor = () => {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [loadingCheckin, setLoadingCheckin] = useState(false);
  const [data, setData] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all'); // 'all' | 'checked' | 'pending'
  const [groupByRoom, setGroupByRoom] = useState(true); // Agrupar por habitación
  
  // Estados para filtros y búsqueda
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  
  // Estados para fechas y servicios
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedService, setSelectedService] = useState('Desayuno');
  
  // Estado para paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    checkedIn: 0,
    pending: 0,
    showSizeChanger: false,
    showQuickJumper: false,
  });

  // Detectar tamaño de pantalla
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;
  const isDesktop = screens.lg;
  const isSmallMobile = screens.xs && !screens.sm;

  // Referencias
  const searchInputRef = useRef(null);

  // Agrupar datos por habitación
  const groupDataByRoom = (items) => {
    const grouped = {};
    items.forEach(item => {
      if (!grouped[item.Habitacion]) {
        grouped[item.Habitacion] = {
          habitacion: item.Habitacion,
          ocupantes: [],
          allCheckedIn: true,
          allPending: true,
          partialChecked: false
        };
      }
      grouped[item.Habitacion].ocupantes.push(item);
    });
    
    // Calcular estados por habitación
    Object.keys(grouped).forEach(room => {
      const ocupantes = grouped[room].ocupantes;
      const checkedInCount = ocupantes.filter(o => o.checkedIn).length;
      const totalCount = ocupantes.length;
      
      grouped[room].allCheckedIn = checkedInCount === totalCount;
      grouped[room].allPending = checkedInCount === 0;
      grouped[room].partialChecked = checkedInCount > 0 && checkedInCount < totalCount;
      grouped[room].checkedInCount = checkedInCount;
      grouped[room].totalCount = totalCount;
    });
    
    return Object.values(grouped).sort((a, b) => a.habitacion - b.habitacion);
  };

  // Datos filtrados según quickFilter
  const filteredData = data.filter(item => {
    if (quickFilter === 'checked') return item.checkedIn;
    if (quickFilter === 'pending') return !item.checkedIn;
    return true;
  });

  // Datos agrupados por habitación
  const groupedData = groupDataByRoom(filteredData);

  // Función para check-in/check-out grupal por habitación
  const handleRoomToggleCheckin = async (roomData) => {
    const { habitacion, ocupantes, allCheckedIn, partialChecked } = roomData;
    
    // Si está parcialmente ocupado, preguntar qué hacer
    if (partialChecked) {
      Modal.confirm({
        title: `Habitación ${habitacion} - Ocupación parcial`,
        content: (
          <div>
            <p>Esta habitación tiene {ocupantes.filter(o => o.checkedIn).length} de {ocupantes.length} personas en comedor.</p>
            <p>¿Qué acción deseas realizar?</p>
          </div>
        ),
        okText: 'Registrar todos',
        cancelText: 'Registrar solo pendientes',
        onOk: () => toggleAllGuestsInRoom(ocupantes, !allCheckedIn),
        onCancel: () => togglePendingGuestsInRoom(ocupantes),
      });
    } else {
      // Todos están igual, simplemente invertir el estado
      const newCheckInState = !allCheckedIn;
      toggleAllGuestsInRoom(ocupantes, newCheckInState);
    }
  };

  const toggleAllGuestsInRoom = async (ocupantes, checkInState) => {
    setLoadingCheckin(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const guest of ocupantes) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${BASE_URL}/api/comedor/checkin`,
          {
            numReserva: guest.NumReserva,
            linea: guest.Linea,
            numOcupante: guest.NumOcupante,
            servicio: selectedService,
            fecha: selectedDate.format('DD/MM/YYYY'),
            checkIn: checkInState
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Error al procesar ${guest.Nombre}:`, error);
      }
    }
    
    if (successCount > 0) {
      // Actualizar datos locales
      const updatedData = data.map(item => {
        const guestToUpdate = ocupantes.find(g => 
          g.NumReserva === item.NumReserva && 
          g.Linea === item.Linea && 
          g.NumOcupante === item.NumOcupante
        );
        if (guestToUpdate) {
          return { ...item, checkedIn: checkInState };
        }
        return item;
      });
      
      setData(updatedData);
      const checkedInCount = updatedData.filter(item => item.checkedIn).length;
      setPagination(prev => ({
        ...prev,
        checkedIn: checkedInCount,
        pending: updatedData.length - checkedInCount
      }));
      
      message.success(
        `${successCount} ${successCount === 1 ? 'persona' : 'personas'} ${checkInState ? 'registradas en comedor' : 'salidas registradas'}${errorCount > 0 ? ` (${errorCount} errores)` : ''}`
      );
    } else {
      message.error('Error al procesar la operación');
    }
    
    setLoadingCheckin(false);
  };

  const togglePendingGuestsInRoom = async (ocupantes) => {
    const pendingGuests = ocupantes.filter(g => !g.checkedIn);
    if (pendingGuests.length === 0) {
      message.info('Todos los huéspedes ya están en comedor');
      return;
    }
    await toggleAllGuestsInRoom(pendingGuests, true);
  };

  // Vista en tarjetas agrupadas por habitación
  const renderGroupedGridView = () => (
    <div style={{ padding: '8px 0' }}>
      <Flex gap={16} vertical>
        {groupedData.map((room) => (
          <Card
            key={room.habitacion}
            style={{
              borderRadius: 20,
              border: room.partialChecked ? '2px solid #faad14' : 
                     room.allCheckedIn ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
              background: room.allCheckedIn ? '#f6ffed' : '#ffffff',
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Header de habitación */}
            <div 
              style={{
                padding: '12px 16px',
                background: room.allCheckedIn ? '#d9f7be' : 
                           room.partialChecked ? '#fff7e6' : '#fafafa',
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
              onClick={() => handleRoomToggleCheckin(room)}
            >
              <Flex align="center" gap={12}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  background: room.allCheckedIn ? '#52c41a' : 
                             room.partialChecked ? '#faad14' : '#d9d9d9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 18
                }}>
                  {room.allCheckedIn ? <CheckCircleOutlined /> : 
                   room.partialChecked ? <ClockCircleOutlined /> : 
                   <TeamOutlined />}
                </div>
                <div>
                  <Text strong style={{ fontSize: 18 }}>
                    Habitación {room.habitacion}
                  </Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {room.checkedInCount}/{room.totalCount} personas en comedor
                    </Text>
                  </div>
                </div>
              </Flex>
              
              <Button
                type={room.allCheckedIn ? "default" : "primary"}
                danger={room.allCheckedIn}
                icon={room.allCheckedIn ? <CloseOutlined /> : <CheckOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRoomToggleCheckin(room);
                }}
                loading={loadingCheckin}
                size="middle"
                style={{ borderRadius: 20 }}
              >
                {room.allCheckedIn ? 'Salir habitación' : 
                 room.partialChecked ? 'Gestionar' : 'Entrar habitación'}
              </Button>
            </div>
            
            {/* Lista de ocupantes */}
            <div style={{ padding: '12px 16px' }}>
              <Flex gap={12} vertical>
                {room.ocupantes.map((guest, idx) => (
                  <div 
                    key={`${guest.NumReserva}-${guest.Linea}-${guest.NumOcupante}`}
                    style={{
                      padding: '8px 12px',
                      background: guest.checkedIn ? '#f6ffed' : '#fff',
                      borderRadius: 12,
                      border: `1px solid ${guest.checkedIn ? '#b7eb8f' : '#f0f0f0'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(guest);
                    }}
                  >
                    <Flex justify="space-between" align="center">
                      <Flex vertical style={{ flex: 1 }}>
                        <Flex align="center" gap={8}>
                          <Text strong style={{ fontSize: 14 }}>
                            {guest.Nombre}
                          </Text>
                          <Tag 
                            color={guest.checkedIn ? 'success' : 'default'}
                            style={{ margin: 0, fontSize: 11 }}
                          >
                            {guest.checkedIn ? 'En comedor' : 'Pendiente'}
                          </Tag>
                        </Flex>
                        <Flex gap={12} style={{ marginTop: 4 }}>
                          <span style={{ fontSize: 12, color: '#666' }}>
                            👤 {guest.Ad}A
                          </span>
                          {guest.Ni > 0 && (
                            <span style={{ fontSize: 12, color: '#666' }}>
                              👶 {guest.Ni}N
                            </span>
                          )}
                          {guest.Bb > 0 && (
                            <span style={{ fontSize: 12, color: '#666' }}>
                              🍼 {guest.Bb}B
                            </span>
                          )}
                          <span style={{ fontSize: 12, color: '#666' }}>
                            📋 {guest.Regimen}
                          </span>
                        </Flex>
                      </Flex>
                      
                      <Button
                        type={guest.checkedIn ? "text" : "link"}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleCheckin(guest);
                        }}
                        loading={loadingCheckin}
                        icon={guest.checkedIn ? <CloseOutlined /> : <CheckOutlined />}
                        style={{ 
                          color: guest.checkedIn ? '#ff4d4f' : '#52c41a',
                          minWidth: 60
                        }}
                      >
                        {guest.checkedIn ? 'Salida' : 'Entrada'}
                      </Button>
                    </Flex>
                  </div>
                ))}
              </Flex>
            </div>
          </Card>
        ))}
      </Flex>
    </div>
  );

  // Vista en tarjetas sin agrupar (individual)
  const renderIndividualGridView = () => (
    <div style={{ padding: '8px 0' }}>
      <Flex gap={12} vertical>
        {filteredData.map((record, index) => (
          <Card
            key={`${record.NumReserva}-${record.Linea}-${record.NumOcupante}`}
            style={{
              borderRadius: 16,
              background: record.checkedIn ? 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)' : '#ffffff',
              border: record.checkedIn ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
            bodyStyle={{ padding: '12px' }}
            onClick={() => handleView(record)}
            hoverable
          >
            <Flex justify="space-between" align="flex-start">
              <Flex vertical style={{ flex: 1 }}>
                <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: '16px' }}>
                    {record.Nombre}
                  </Text>
                  <Tag 
                    color={record.checkedIn ? 'success' : 'warning'}
                    style={{ borderRadius: 20, margin: 0 }}
                  >
                    {record.checkedIn ? 'En comedor' : 'Pendiente'}
                  </Tag>
                </Flex>
                
                <Flex gap={8} wrap="wrap" style={{ marginBottom: 8 }}>
                  <Tag color="blue" style={{ borderRadius: 12 }}>
                    Hab {record.Habitacion}
                  </Tag>
                  <Tag color="purple" style={{ borderRadius: 12 }}>
                    {record.Regimen}
                  </Tag>
                </Flex>
                
                <Flex gap={12} style={{ marginTop: 4 }}>
                  <span style={{ fontSize: '13px', color: '#666' }}>
                    👤 {record.Ad} Adulto{record.Ad !== 1 ? 's' : ''}
                  </span>
                  {record.Ni > 0 && (
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      👶 {record.Ni} Niño{record.Ni !== 1 ? 's' : ''}
                    </span>
                  )}
                  {record.Bb > 0 && (
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      🍼 {record.Bb} Bebé{record.Bb !== 1 ? 's' : ''}
                    </span>
                  )}
                </Flex>
              </Flex>
              
              <Button
                type={record.checkedIn ? "default" : "primary"}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleCheckin(record);
                }}
                loading={loadingCheckin}
                style={{
                  borderRadius: 20,
                  minWidth: 70,
                  fontWeight: 500
                }}
              >
                {record.checkedIn ? 'Salida' : 'Entrada'}
              </Button>
            </Flex>
          </Card>
        ))}
      </Flex>
    </div>
  );

  // Barra de búsqueda mejorada para móvil
  const renderMobileSearchBar = () => (
    <div style={{ marginBottom: 12 }}>
      <Flex gap={8} align="center">
        <div style={{ flex: 1 }}>
          <Input
            ref={searchInputRef}
            placeholder="Buscar huésped..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
            onClear={handleResetSearch}
            prefix={<SearchOutlined />}
            size="large"
            style={{ borderRadius: 24 }}
          />
        </div>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
          size="large"
          style={{ borderRadius: 24, width: 48 }}
        />
      </Flex>
      
      {searchExpanded && (
        <div style={{ marginTop: 12 }}>
          <Card size="small" style={{ borderRadius: 16 }}>
            <Flex vertical gap={12}>
              <Flex justify="space-between">
                <Text strong>Filtros rápidos</Text>
                <Button 
                  type="link" 
                  size="small" 
                  onClick={handleResetSearch}
                  style={{ padding: 0 }}
                >
                  Limpiar todo
                </Button>
              </Flex>
              
              <Segmented
                block
                value={quickFilter}
                onChange={setQuickFilter}
                options={[
                  { label: 'Todos', value: 'all' },
                  { label: 'En comedor', value: 'checked' },
                  { label: 'Pendientes', value: 'pending' },
                ]}
                style={{ marginBottom: 8 }}
              />
              
              <Row gutter={8}>
                <Col span={12}>
                  <DatePicker
                    value={selectedDate}
                    onChange={setSelectedDate}
                    format="DD/MM/YYYY"
                    size="small"
                    style={{ width: '100%', borderRadius: 20 }}
                    suffixIcon={<CalendarOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Select
                    value={selectedService}
                    onChange={setSelectedService}
                    size="small"
                    style={{ width: '100%', borderRadius: 20 }}
                    options={[
                      { value: 'Desayuno', label: '☕ Desayuno' },
                      { value: 'Almuerzo', label: '🍽️ Almuerzo' },
                      { value: 'Cena', label: '🌙 Cena' },
                    ]}
                  />
                </Col>
              </Row>
              
              <Divider style={{ margin: '8px 0' }} />
              
              <Flex justify="space-between" align="center">
                <Text strong>Agrupar por habitación</Text>
                <Segmented
                  size="small"
                  value={groupByRoom}
                  onChange={setGroupByRoom}
                  options={[
                    { value: true, icon: <GroupOutlined />, label: 'Grupos' },
                    { value: false, icon: <UserOutlined />, label: 'Individual' },
                  ]}
                />
              </Flex>
            </Flex>
          </Card>
        </div>
      )}
      
      <Button
        type="text"
        icon={searchExpanded ? <UpOutlined /> : <FilterOutlined />}
        onClick={() => setSearchExpanded(!searchExpanded)}
        size="small"
        style={{ 
          marginTop: 8, 
          marginLeft: 8,
          color: '#1890ff'
        }}
      >
        {searchExpanded ? 'Ocultar filtros' : 'Más filtros'}
      </Button>
    </div>
  );

  // Estadísticas mejoradas para móvil
  const renderMobileStats = () => {
    const habitaciones = new Set(data.map(d => d.Habitacion)).size;
    
    return (
      <Card 
        style={{ 
          marginBottom: 16, 
          borderRadius: 20,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Row gutter={[12, 12]}>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                {habitaciones}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                <HomeOutlined /> Hab.
              </div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                {pagination.total}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                <TeamOutlined /> Total
              </div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                {pagination.checkedIn}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                <CheckCircleOutlined /> En comedor
              </div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                {pagination.pending}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                <ClockCircleOutlined /> Pend.
              </div>
            </div>
          </Col>
        </Row>
        
        <Progress 
          percent={pagination.total ? (pagination.checkedIn / pagination.total * 100) : 0}
          showInfo={false}
          strokeColor="#52c41a"
          trailColor="rgba(255,255,255,0.3)"
          style={{ marginTop: 12 }}
        />
      </Card>
    );
  };

  // Selector de vista (lista/tarjetas)
  const renderViewToggle = () => (
    <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
      <Flex gap={8} align="center">
        <Text strong style={{ fontSize: 15 }}>
          {groupByRoom ? groupedData.length : filteredData.length} 
          {groupByRoom ? ' habitaciones' : ' huéspedes'}
        </Text>
        {!isMobile && (
          <Segmented
            size="small"
            value={groupByRoom}
            onChange={setGroupByRoom}
            options={[
              { value: true, icon: <GroupOutlined />, label: 'Agrupar por habitación' },
              { value: false, icon: <UserOutlined />, label: 'Vista individual' },
            ]}
          />
        )}
      </Flex>
      <Segmented
        size="small"
        value={viewMode}
        onChange={setViewMode}
        options={[
          { value: 'list', icon: <MenuOutlined /> },
          { value: 'grid', icon: <AppstoreOutlined /> },
        ]}
      />
    </Flex>
  );

  // Render principal del contenido
  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ padding: 24 }}>
          <Skeleton active avatar paragraph={{ rows: 4 }} />
          <Skeleton active avatar paragraph={{ rows: 4 }} style={{ marginTop: 16 }} />
          <Skeleton active avatar paragraph={{ rows: 4 }} style={{ marginTop: 16 }} />
        </div>
      );
    }
    
    if (filteredData.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              {searchText 
                ? `No se encontró "${searchText}"` 
                : 'No hay huéspedes para este servicio'}
            </span>
          }
          style={{ marginTop: 40 }}
        >
          {searchText && (
            <Button type="link" onClick={handleResetSearch}>
              Limpiar búsqueda
            </Button>
          )}
        </Empty>
      );
    }
    
    if (isMobile) {
      if (viewMode === 'grid') {
        return groupByRoom ? renderGroupedGridView() : renderIndividualGridView();
      }
      return renderMobileListView();
    }
    
    // Tablet/Desktop view
    return renderDesktopView();
  };

  const renderMobileListView = () => (
    <Table
      columns={mobileColumns}
      dataSource={filteredData}
      rowKey={(record) => `${record.NumReserva}-${record.Linea}-${record.NumOcupante}`}
      loading={loading}
      pagination={{
        ...pagination,
        size: "small",
        simple: true,
        pageSize: 15,
        showSizeChanger: false
      }}
      size="small"
      showHeader={false}
      style={{ margin: '-8px' }}
      onRow={(record) => ({
        onClick: () => handleView(record),
      })}
    />
  );

  const renderDesktopView = () => {
    if (groupByRoom) {
      return (
        <Collapse 
          defaultActiveKey={groupedData.map(r => String(r.habitacion))}
          style={{ background: 'transparent' }}
          expandIconPosition="end"
        >
          {groupedData.map((room) => (
            <Panel
              key={room.habitacion}
              header={
                <Flex justify="space-between" align="center" style={{ width: '100%', paddingRight: 16 }}>
                  <Space>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: 16,
                      background: room.allCheckedIn ? '#52c41a' : 
                                 room.partialChecked ? '#faad14' : '#d9d9d9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff'
                    }}>
                      {room.allCheckedIn ? <CheckCircleOutlined /> : 
                       room.partialChecked ? <ClockCircleOutlined /> : 
                       <TeamOutlined />}
                    </div>
                    <Text strong style={{ fontSize: 16 }}>Habitación {room.habitacion}</Text>
                    <Badge 
                      count={`${room.checkedInCount}/${room.totalCount}`} 
                      showZero
                      style={{ backgroundColor: room.allCheckedIn ? '#52c41a' : '#1890ff' }}
                    />
                  </Space>
                  <Button
                    type={room.allCheckedIn ? "default" : "primary"}
                    danger={room.allCheckedIn}
                    icon={room.allCheckedIn ? <CloseOutlined /> : <CheckOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRoomToggleCheckin(room);
                    }}
                    loading={loadingCheckin}
                    size="small"
                  >
                    {room.allCheckedIn ? 'Salir habitación' : 
                     room.partialChecked ? 'Gestionar' : 'Entrar habitación'}
                  </Button>
                </Flex>
              }
            >
              <Table
                columns={desktopColumns}
                dataSource={room.ocupantes}
                rowKey={(record) => `${record.NumReserva}-${record.Linea}-${record.NumOcupante}`}
                pagination={false}
                size="small"
              />
            </Panel>
          ))}
        </Collapse>
      );
    }
    
    return (
      <Table
        columns={desktopColumns}
        dataSource={filteredData}
        rowKey={(record) => `${record.NumReserva}-${record.Linea}-${record.NumOcupante}`}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          ...pagination,
          pageSize: isTablet ? 10 : 15,
          showSizeChanger: !isMobile,
          showTotal: (total) => `${total} huéspedes`
        }}
        size="middle"
      />
    );
  };

  // Desktop columns (sin cambios)
  const desktopColumns = [
    {
      title: 'Estado',
      dataIndex: 'checkedIn',
      key: 'checkedIn',
      width: 100,
      render: (checkedIn) => (
        checkedIn ? (
          <Badge status="success" text="En comedor" />
        ) : (
          <Badge status="default" text="Pendiente" />
        )
      ),
      filters: [
        { text: 'En comedor', value: true },
        { text: 'Pendiente', value: false },
      ],
      onFilter: (value, record) => record.checkedIn === value,
    },
    {
      title: 'Nombre',
      dataIndex: 'Nombre',
      key: 'Nombre',
      width: 200,
      sorter: (a, b) => a.Nombre?.localeCompare(b.Nombre || '') || 0,
      render: (nombre) => <Text strong>{nombre}</Text>,
    },
    {
      title: 'Ocupantes',
      key: 'occupants',
      width: 120,
      render: (_, record) => (
        <Space size={4}>
          <Tag>{record.Ad}A</Tag>
          {record.Ni > 0 && <Tag color="green">{record.Ni}N</Tag>}
          {record.Bb > 0 && <Tag color="orange">{record.Bb}B</Tag>}
        </Space>
      ),
    },
    {
      title: 'Régimen',
      dataIndex: 'Regimen',
      key: 'Regimen',
      width: 100,
      render: (regimen) => <Tag color="purple">{regimen}</Tag>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title={record.checkedIn ? "Registrar salida" : "Registrar entrada"}>
            <Button
              type={record.checkedIn ? "default" : "primary"}
              icon={record.checkedIn ? <CloseOutlined /> : <CheckOutlined />}
              size="small"
              onClick={() => handleToggleCheckin(record)}
              loading={loadingCheckin}
            >
              {record.checkedIn ? 'Salida' : 'Entrada'}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const mobileColumns = [
    {
      title: '',
      dataIndex: 'checkedIn',
      key: 'status',
      width: 40,
      render: (checkedIn) => (
        <div style={{ 
          width: 32, 
          height: 32, 
          borderRadius: 16, 
          background: checkedIn ? '#52c41a20' : '#ff4d4f20',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {checkedIn ? (
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
          ) : (
            <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: 20 }} />
          )}
        </div>
      ),
    },
    {
      title: 'Huésped',
      dataIndex: 'Nombre',
      key: 'Nombre',
      render: (nombre, record) => (
        <div>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: 4 }}>
            {nombre}
          </div>
          <Flex gap={8} wrap="wrap">
            <Tag color="blue" style={{ margin: 0, fontSize: '11px', borderRadius: 12 }}>
              📍 Hab {record.Habitacion}
            </Tag>
            <Tag color="purple" style={{ margin: 0, fontSize: '11px', borderRadius: 12 }}>
              {record.Regimen}
            </Tag>
          </Flex>
          <Flex gap={12} style={{ marginTop: 4 }}>
            <span style={{ fontSize: '12px', color: '#666' }}>👤 {record.Ad}A</span>
            {record.Ni > 0 && <span style={{ fontSize: '12px', color: '#666' }}>👶 {record.Ni}N</span>}
            {record.Bb > 0 && <span style={{ fontSize: '12px', color: '#666' }}>🍼 {record.Bb}B</span>}
          </Flex>
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 70,
      render: (_, record) => (
        <Button
          type={record.checkedIn ? "default" : "primary"}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleToggleCheckin(record);
          }}
          loading={loadingCheckin}
          style={{ borderRadius: 20, minWidth: 60 }}
        >
          {record.checkedIn ? 'Salida' : 'Entrada'}
        </Button>
      ),
    },
  ];

  // Funciones existentes (mantener igual)
  const fetchComedorData = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/comedor`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fecha: selectedDate.format('DD/MM/YYYY'),
          servicio: selectedService,
          ...params,
          search: searchText
        }
      });
      if (response.data.success) {
        setData(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.totalRecords,
          checkedIn: response.data.pagination.checkedIn || 0,
          pending: response.data.pagination.pending || 0
        }));
      }
    } catch (error) {
      message.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (record) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/comedor/guest/detail`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          numReserva: record.NumReserva,
          linea: record.Linea,
          numOcupante: record.NumOcupante,
          fecha: selectedDate.format('DD/MM/YYYY'),
          servicio: selectedService
        }
      });
      if (response.data.success) {
        setSelectedGuest(response.data.data);
        if (isMobile) {
          setDrawerVisible(true);
        } else {
          setModalVisible(true);
        }
      }
    } catch (error) {
      message.error('Error al cargar detalles');
    }
  };

  const handleToggleCheckin = async (record) => {
    setLoadingCheckin(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/comedor/checkin`,
        {
          numReserva: record.NumReserva,
          linea: record.Linea,
          numOcupante: record.NumOcupante,
          servicio: selectedService,
          fecha: selectedDate.format('DD/MM/YYYY'),
          checkIn: !record.checkedIn
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const updatedData = data.map(item => {
          if (item.NumReserva === record.NumReserva && 
              item.Linea === record.Linea && 
              item.NumOcupante === record.NumOcupante) {
            return { ...item, checkedIn: !record.checkedIn };
          }
          return item;
        });
        setData(updatedData);
        const checkedInCount = updatedData.filter(item => item.checkedIn).length;
        setPagination(prev => ({
          ...prev,
          checkedIn: checkedInCount,
          pending: updatedData.length - checkedInCount
        }));
        message.success(!record.checkedIn ? 'Entrada registrada' : 'Salida registrada');
        if (isMobile) setDrawerVisible(false);
        else setModalVisible(false);
      }
    } catch (error) {
      message.error('Error al actualizar');
    } finally {
      setLoadingCheckin(false);
    }
  };

  const handleSearch = () => {
    if (searchText.trim()) {
      setSearchLoading(true);
      setPagination({ ...pagination, current: 1 });
      fetchComedorData({ current: 1 });
    } else {
      handleResetSearch();
    }
  };

  const handleResetSearch = () => {
    setSearchText('');
    setQuickFilter('all');
    setSearchExpanded(false);
    fetchComedorData();
  };

  const handleRefresh = () => {
    fetchComedorData();
    message.success('Actualizado');
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    fetchComedorData({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sortField: sorter.field,
      sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
    });
  };

  useEffect(() => {
    fetchComedorData();
  }, [selectedDate, selectedService]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: { borderRadiusLG: 20 },
          Button: { borderRadius: 20 },
          Tag: { borderRadiusSM: 12 },
        }
      }}
    >
      <PageContainer
        title={isSmallMobile ? "🍽️ Comedor" : "Comedor"}
        subtitle={!isSmallMobile ? `${selectedService} - ${selectedDate.format('DD/MM/YYYY')}` : undefined}
        icon={<TeamOutlined />}
        loading={loading}
        onRefresh={handleRefresh}
        badgeText={`${pagination.pending} pendientes`}
        extra={
          !isMobile && (
            <Space>
              <Button icon={<ExportOutlined />} onClick={() => message.info('Exportación en desarrollo')}>
                Exportar
              </Button>
            </Space>
          )
        }
      >
        {isMobile ? (
          <>
            {renderMobileStats()}
            {renderMobileSearchBar()}
            {renderViewToggle()}
            {renderContent()}
            
            <FloatButton.Group
              trigger="click"
              icon={<SettingOutlined />}
              style={{ bottom: 80, right: 16 }}
            >
              <FloatButton 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
                tooltip="Actualizar"
              />
              <FloatButton 
                icon={<ExportOutlined />} 
                onClick={() => message.info('Exportación en desarrollo')}
                tooltip="Exportar"
              />
            </FloatButton.Group>
          </>
        ) : (
          <>
            <Card style={{ marginBottom: 16, borderRadius: 16 }}>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong>Fecha</Text>
                    <DatePicker
                      value={selectedDate}
                      onChange={setSelectedDate}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                    />
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong>Servicio</Text>
                    <Select
                      value={selectedService}
                      onChange={setSelectedService}
                      style={{ width: '100%' }}
                      options={[
                        { value: 'Desayuno', label: '☕ Desayuno' },
                        { value: 'Almuerzo', label: '🍽️ Almuerzo' },
                        { value: 'Cena', label: '🌙 Cena' },
                      ]}
                    />
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong>Buscar</Text>
                    <Input
                      placeholder="Nombre, habitación..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onPressEnter={handleSearch}
                      suffix={
                        <SearchOutlined 
                          onClick={handleSearch}
                          style={{ cursor: 'pointer' }}
                        />
                      }
                    />
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong>Agrupación</Text>
                    <Segmented
                      block
                      value={groupByRoom}
                      onChange={setGroupByRoom}
                      options={[
                        { value: true, icon: <GroupOutlined />, label: 'Por habitación' },
                        { value: false, icon: <UserOutlined />, label: 'Individual' },
                      ]}
                    />
                  </Space>
                </Col>
              </Row>
            </Card>
            
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small" style={{ borderRadius: 12, textAlign: 'center' }}>
                  <Statistic
                    title="Habitaciones"
                    value={new Set(data.map(d => d.Habitacion)).size}
                    prefix={<HomeOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ borderRadius: 12, textAlign: 'center' }}>
                  <Statistic
                    title="Total huéspedes"
                    value={pagination.total}
                    prefix={<TeamOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ borderRadius: 12, textAlign: 'center' }}>
                  <Statistic
                    title="En comedor"
                    value={pagination.checkedIn}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ borderRadius: 12, textAlign: 'center' }}>
                  <Statistic
                    title="Pendientes"
                    value={pagination.pending}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
            </Row>
            
            <Card style={{ borderRadius: 16 }}>
              <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
                <Segmented
                  value={quickFilter}
                  onChange={setQuickFilter}
                  options={[
                    { label: 'Todos', value: 'all' },
                    { label: 'En comedor', value: 'checked' },
                    { label: 'Pendientes', value: 'pending' },
                  ]}
                />
                <Button icon={<ReloadOutlined />} onClick={handleRefresh} size="small">
                  Actualizar
                </Button>
              </Flex>
              {renderContent()}
            </Card>
          </>
        )}
        
        {/* Modal para desktop/tablet */}
        <Modal
          title="Detalle del huésped"
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={500}
          centered
        >
          {selectedGuest && renderGuestDetail()}
        </Modal>
        
        {/* Drawer para móvil */}
        <Drawer
          title="Detalle del huésped"
          placement="bottom"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          height="auto"
          style={{ borderRadius: '20px 20px 0 0' }}
          closeIcon={<CloseOutlined />}
        >
          {selectedGuest && renderGuestDetail(true)}
        </Drawer>
      </PageContainer>
    </ConfigProvider>
  );
  
  function renderGuestDetail(isMobileView = false) {
    if (!selectedGuest) return <Spin />;
    
    return (
      <div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Avatar
            size={isMobileView ? 64 : 80}
            style={{ 
              backgroundColor: selectedGuest.checkedIn ? '#52c41a' : '#1890ff',
              marginBottom: 12
            }}
            icon={<UserOutlined />}
          />
          <Title level={isMobileView ? 5 : 4} style={{ marginBottom: 4 }}>
            {selectedGuest.Nombre}
          </Title>
          <Tag color="blue">Habitación {selectedGuest.Habitacion}</Tag>
        </div>
        
        <List
          size="small"
          dataSource={[
            { label: 'Estado', value: selectedGuest.checkedIn ? '✅ En comedor' : '⏳ Pendiente' },
            { label: 'Régimen', value: selectedGuest.Regimen },
            { label: 'Adultos', value: selectedGuest.Ad },
            { label: 'Niños', value: selectedGuest.Ni },
            { label: 'Bebés', value: selectedGuest.Bb },
            { label: 'Entrada', value: dayjs(selectedGuest.Entrada).format('DD/MM/YYYY') },
            { label: 'Salida', value: dayjs(selectedGuest.Salida).format('DD/MM/YYYY') },
            { label: 'Reserva', value: selectedGuest.NumReserva },
          ]}
          renderItem={(item) => (
            <List.Item style={{ padding: '12px 0' }}>
              <Flex justify="space-between" style={{ width: '100%' }}>
                <Text type="secondary">{item.label}</Text>
                <Text strong>{item.value}</Text>
              </Flex>
            </List.Item>
          )}
        />
        
        {(selectedGuest.TodoIncluido === 'X' || selectedGuest.BebidaIncluida === 'X') && (
          <>
            <Divider />
            {selectedGuest.TodoIncluido === 'X' && (
              <Alert
                message="Todo Incluido"
                description="Régimen todo incluido activo"
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
              />
            )}
            {selectedGuest.BebidaIncluida === 'X' && (
              <Alert
                message="Bebida Incluida"
                description="Bebida incluida en el servicio"
                type="info"
                showIcon
              />
            )}
          </>
        )}
        
        <Divider />
        
        <Button
          type={selectedGuest.checkedIn ? "primary" : "primary"}
          danger={selectedGuest.checkedIn}
          icon={selectedGuest.checkedIn ? <CloseOutlined /> : <CheckOutlined />}
          onClick={() => handleToggleCheckin(selectedGuest)}
          loading={loadingCheckin}
          size="large"
          block
          style={{ 
            borderRadius: 24,
            height: 48,
            fontSize: 16
          }}
        >
          {selectedGuest.checkedIn ? 'Registrar Salida' : 'Registrar Entrada'}
        </Button>
      </div>
    );
  }
};

export default Comedor;